/**
 * Setup module registry (unified-viewer-setup-design.md §1.4).
 *
 * ONE place maps every setup module id (across every product mode — D5) to a human label
 * and, when implemented, a component. `productConfig.setup.modules` (per mode) picks which
 * ids appear and which are `{ stub: true }`. The shell renders the real component when one is
 * registered here AND the entry isn't stubbed; otherwise it renders SetupModuleStub. So future
 * work is "register a component + drop the stub flag" — no shell changes.
 *
 * Cloud v1 implements: display (shared), users, cameras. Everything else resolves to a stub.
 *
 * NOTE (David 2026-07-20): a cloud "device" is the actual edge NVR box (self-registered into
 * cloudapi), NOT the local Java "device-type" container. They get DISTINCT labels here
 * ("Cloud NVRs" vs "Devices") so the unified Setup never conflates the two meanings.
 */
import DisplaySetup from './DisplaySetup';
import CloudUserSetup from './CloudUserSetup';
import CloudCameraSetup from './CloudCameraSetup';
import CloudCustomerSetup from './CloudCustomerSetup';
import CloudSiteSetup from './CloudSiteSetup';
import CloudDeviceSetup from './CloudDeviceSetup';

// Human labels + the RIGHT each module requires, for every module id, all modes.
//
// Per-module gating (David 2026-08-05): the Setup SHELL is ALWAYS open to every user — we gate the
// individual modules, not the whole menu. `right` is the right needed to see a module's tab:
//   - Display (and any future viewer-CLIENT pref) carries NO right → it is NEVER gated; every user sees it.
//   - The device/NVR config modules carry 'ADMIN' → only administrators see those tabs.
// A non-admin therefore opens Setup and simply sees fewer tabs (Display only), not a locked-out screen.
// The right is module-intrinsic (Camera config is admin-level regardless of product), so it lives here.
export const SETUP_MODULES = {
  // Nav tab labels are SINGULAR (David 2026-07-21) — compact + consistent with "Display"; the
  // in-screen back link stays plural ("‹ Customers") since it points back to the list.
  display:    { label: 'Display' },                    // viewer prefs — NEVER gated (every user)
  cameras:    { label: 'Camera', right: 'ADMIN' },
  users:      { label: 'User', right: 'ADMIN' },
  devices:    { label: 'Device', right: 'ADMIN' },     // cloud: the edge NVR boxes (self-registered)
  sites:      { label: 'Site', right: 'ADMIN' },
  customers:  { label: 'Customer', right: 'ADMIN', access: { master: 'edit', local: 'hidden' } },  // #86bbm6j8w: org mgmt is master-only — a local admin doesn't see the Customer tab at all
  system:     { label: 'System', right: 'ADMIN' },
  device:     { label: 'Devices', right: 'ADMIN' },    // local: device-type container
  dtime:      { label: 'Date / Time', right: 'ADMIN' },
  network:    { label: 'Network', right: 'ADMIN' },
  videostore: { label: 'Video Store', right: 'ADMIN' },
  report:     { label: 'Report', right: 'ADMIN' },
  pos:        { label: 'POS', right: 'ADMIN' },
  enterprise: { label: 'Enterprise', right: 'ADMIN' },
};

/** The right a setup module requires to be visible (undefined = ungated, e.g. Display). */
export const getSetupRight = (id) => (SETUP_MODULES[id] && SETUP_MODULES[id].right) || null;

// Real (implemented) components. Any id not present here renders SetupModuleStub.
const COMPONENTS = {
  display: DisplaySetup,
  users: CloudUserSetup,
  cameras: CloudCameraSetup,
  customers: CloudCustomerSetup,
  sites: CloudSiteSetup,
  devices: CloudDeviceSetup,
};

export const getSetupLabel = (id) => (SETUP_MODULES[id] && SETUP_MODULES[id].label) || id;
export const getSetupComponent = (id) => COMPONENTS[id] || null;
